import { ServiceProviderError } from '@takeshape/errors';
import { getRef, isObjectSchema, isRestResolver, refItemToShapeSchema } from '@takeshape/schema';
import { value } from '@takeshape/util';
import omit from 'lodash/omit.js';
import set from 'lodash/set.js';
import unset from 'lodash/unset.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { isOAuth2TokenResponse } from "../types.js";
import { getAuthInfoCacheExpirationForClientCredentials, getAuthInfoRequestConfigForClientCredentials } from "../util/auth-info.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
const title = 'Yotpo UGC';
const namespace = 'YotpoUgc';
const providerId = 'yotpoUgc';
const endpoint = 'https://api.yotpo.com';
export const specUrl = 'https://raw.githubusercontent.com/takeshape/openapi-specs/75f77764b6c618f4f17967c63098ad26f39cab27/specs/yotpo/ugc.yml';
export function getAuthInfoResult(_, response, { resolver, isHealthCheck } = {}) {
    if (!isOAuth2TokenResponse(response)) {
        throw new ServiceProviderError(`${title} - invalid auth response`);
    }
    const authLocation = resolver && 'options' in resolver && resolver.options && 'authLocation' in resolver.options
        ? resolver?.options?.authLocation
        : undefined;
    if (Boolean(isHealthCheck) || authLocation === 'searchParams') {
        return [[['searchParams', 'utoken'], response.access_token]];
    }
    if (authLocation === 'body') {
        return [[['body', 'utoken'], response.access_token]];
    }
    if (authLocation === 'headers') {
        return [[['headers', 'Authorization'], `${response.token_type} ${response.access_token}`]];
    }
    return [];
}
function removeArgs(objectSchema, names) {
    objectSchema.properties = omit(objectSchema.properties, names);
    objectSchema.required &&= objectSchema.required.filter((name) => !names.includes(name));
}
/**
 * Transform query mutates the given query
 */
function transformQuery(layerWithServices, prop, name) {
    let utokenInBody = false;
    let appKeyInBody;
    const query = layerWithServices[prop][name];
    if (isObjectSchema(query.args)) {
        removeArgs(query.args, ['utoken', 'app_key']);
        let { input } = query.args.properties;
        const ref = input && getRef(layerWithServices, input);
        if (ref) {
            const shapeSchema = refItemToShapeSchema(layerWithServices, ref);
            if (shapeSchema) {
                input = shapeSchema;
            }
        }
        if (isObjectSchema(input)) {
            if (input.properties.utoken) {
                utokenInBody = true;
            }
            if (input.properties.appkey) {
                appKeyInBody = 'appkey';
            }
            else if (input.properties.app_key) {
                appKeyInBody = 'app_key';
            }
            removeArgs(input, ['utoken', 'appkey', 'app_key']);
        }
    }
    const { resolver } = query;
    if (isRestResolver(resolver)) {
        if (typeof resolver.path === 'object') {
            const index = resolver.path.ops.findIndex((op) => op.path === 'app_key');
            if (index !== -1) {
                resolver.path.ops[index] = {
                    path: 'app_key',
                    mapping: '$service.options.storeId'
                };
            }
        }
        if (resolver.headers?.ops.find((op) => op.path === 'authorization')) {
            set(resolver, ['options', 'authLocation'], 'headers');
        }
        else if (resolver.searchParams?.ops.find((op) => op.path === 'utoken')) {
            resolver.searchParams.ops = resolver.searchParams.ops.filter((op) => op.path !== 'utoken');
            if (resolver.searchParams?.ops.length === 0) {
                unset(resolver, ['searchParams']);
            }
            else {
                unset(resolver.searchParams, ['serialize', 'paths', 'utoken']);
            }
            set(resolver, ['options', 'authLocation'], 'searchParams');
        }
        else if (utokenInBody) {
            set(resolver, ['options', 'authLocation'], 'body');
        }
        if (resolver.body && appKeyInBody) {
            resolver.body.ops.push({
                path: appKeyInBody,
                mapping: '$service.options.storeId'
            });
        }
    }
}
export function schemaTransform(layerSchema, serviceConfig) {
    const layerWithServices = {
        ...layerSchema,
        services: { [serviceConfig.id]: serviceConfig }
    };
    for (const prop of ['queries', 'mutations']) {
        for (const name of Object.keys(layerSchema[prop])) {
            transformQuery(layerWithServices, prop, name);
        }
    }
    return layerSchema;
}
export function specTransform(spec) {
    return { ...spec, paths: omit(spec.paths, '/oauth/token') };
}
const serviceProvider = {
    name: providerId,
    title,
    description: 'Yotpo UGC API',
    categories: ['ecommerce'],
    links: [{ title: 'Homepage', url: 'https://www.yotpo.com/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('yotpo-ugc'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$',
                default: 'yotpo-ugc'
            },
            title: {
                title: 'Name',
                type: 'string',
                default: title
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: namespace
            },
            storeId: {
                title: 'Store Id (App Key)',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    secretKey: {
                        title: 'Secret Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['secretKey']
            }
        },
        required: ['title', 'id', 'namespace', 'storeId', 'authentication']
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'storeId', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            storeId: {
                widget: 'singleLineText',
                instructions: 'Find the Store Id (App Key) in your [General Settings](https://settings.yotpo.com/#/general_settings)'
            },
            authentication: {
                order: ['secretKey'],
                properties: {
                    secretKey: {
                        widget: 'sensitiveField',
                        instructions: 'Find this in your [General Settings](https://settings.yotpo.com/#/general_settings)'
                    }
                }
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? namespace,
            provider: providerId,
            serviceType: 'openapi',
            authenticationType: 'oauth2',
            options: {
                endpoint,
                spec: specUrl,
                storeId: formData.storeId,
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        const { authentication } = formData;
        if (!authentication && !formData.storeId) {
            return config;
        }
        config.authentication = {
            type: 'oauth2',
            grantType: 'clientCredentials',
            accessTokenUrl: 'https://api.yotpo.com/oauth/token',
            clientId: formData.storeId,
            clientSecret: fixObfuscatedProp(authentication?.secretKey)
        };
        return config;
    },
    getAuthInfoCacheExpiration: getAuthInfoCacheExpirationForClientCredentials(title),
    getAuthInfoRequestConfig: getAuthInfoRequestConfigForClientCredentials(title),
    getAuthInfoResult,
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    schemaTransform,
    specTransform,
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: (serviceConfig) => `${serviceConfig.options.endpoint}/v1/apps/${serviceConfig.options.storeId}/reviews`
    })
};
export default serviceProvider;
